import { Component, inject, input, OnInit } from '@angular/core';
import { ZiroFormAbstractDirective } from '../../../forms/ziro-form-abstract.directive';
import { ZiroFormStateService } from '../../../forms/ziro-form-state.service';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { ZiroFormConfig, ZiroFormsValidationState } from '../../../forms/ziro-forms-models';
import {
  MicrosoftSecurityGroupOption,
  MicrosoftSecurityGroupRef,
  MicrosoftSecurityGroupResult,
} from '../../../shared/models/generated/ziroModels';
import {
  BottomNavService,
  BottomNavUpdateButtonsList,
  BottomNavUpdateState,
} from '../../../shared/bottom-nav/bottom-nav.service';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { ButtonStyles, ButtonTypes } from '../../../button/button.component';
import { MicrosoftSecurityGroupSettingsResource } from '../../../shared/resources/microsoft-security-group-settings.resource';
import { ToastService } from '../../../shared/services/toast.service';
import { ZiroSelectConfig, ZiroSelectOption } from '../../../forms/fields/select/ziro-select.component';
import { finalize, map, tap } from 'rxjs/operators';
import { ToastTypes } from '../../../shared/services/abstract/toast.service.abstract';
import { ZiroModalService } from '../../../shared/services/ziro-modal.service';
import { TranslateService } from '@ngx-translate/core';
import { MicrosoftSecurityGroupsTenantSearchResource } from '../../resources/microsoft-security-groups-tenant-search.resource';

export interface MsSecurityGroupManagementEntity {
  readOnlySecurityGroupList: MicrosoftSecurityGroupOption[];
  writeSecurityGroupList: MicrosoftSecurityGroupOption[];
}

export interface MsSecurityGroupManagementFormData {
  readOnlySecurityGroupList: MsSecurityGroupManagementSelectFormData[];
  writeSecurityGroupList: MsSecurityGroupManagementSelectFormData[];
}

interface MsSecurityGroupManagementSelectFormData {
  label: string;
  value: MicrosoftSecurityGroupRef;
}

interface SecurityGroupAsyncSearchParams {
  assignedOnly: boolean;
  entityKey: keyof MsSecurityGroupManagementEntity;
}

@Component({
  selector: 'ziro-security-group-management-form',
  templateUrl: './security-group-management-form.component.html',
  standalone: false,
})
export class SecurityGroupManagementFormComponent
  extends ZiroFormAbstractDirective<MsSecurityGroupManagementEntity, MsSecurityGroupManagementFormData>
  implements OnInit
{
  private _bottomNavService = inject(BottomNavService);
  private _toastService = inject(ToastService);
  private _microsoftSecurityGroupsSettingsResource = inject(MicrosoftSecurityGroupSettingsResource);
  private _microsoftSecurityGroupsSearchResource = inject(MicrosoftSecurityGroupsTenantSearchResource);
  private _ziroModalService = inject(ZiroModalService);
  private _translateService = inject(TranslateService);
  protected _ziroFormStateService: ZiroFormStateService;

  readonly securityOptions = input<string[]>();
  readonly tenantId = input<number>();
  readonly securityGroups = input<MicrosoftSecurityGroupOption[]>();

  formConfig: ZiroFormConfig;

  private _subscriptions = new Subscription();
  private _overlappingErrorMessage = '';
  private _validators = {
    noOverlaps: (
      writeSecurityGroupList: MsSecurityGroupManagementSelectFormData[],
      fieldValues: MsSecurityGroupManagementSelectFormData[],
    ): ZiroFormsValidationState => {
      const writeGroupIds = writeSecurityGroupList.map(
        (group: MsSecurityGroupManagementSelectFormData) => group.value.id,
      );

      const overlappingGroups = fieldValues.filter((fieldValue: MsSecurityGroupManagementSelectFormData) =>
        writeGroupIds.includes(fieldValue.value.id),
      );
      this._overlappingErrorMessage = this._translateService.instant(
        'tkey;admin.microsoft.security_group_management.form.error.overlapping',
      );
      overlappingGroups.forEach((group: MsSecurityGroupManagementSelectFormData) => {
        this._overlappingErrorMessage += ` <strong>${group.label}</strong>,`;
      });
      this._overlappingErrorMessage = this._overlappingErrorMessage.substring(
        0,
        this._overlappingErrorMessage.length - 1,
      );

      return overlappingGroups.length ? ZiroFormsValidationState.INVALID : ZiroFormsValidationState.VALID;
    },
    noDeletedValues: (securityGroups: MsSecurityGroupManagementSelectFormData[]): ZiroFormsValidationState => {
      const hasDeletedValue = securityGroups.length > 0 && securityGroups.some((group) => !group.value.displayName);
      return hasDeletedValue ? ZiroFormsValidationState.INVALID : ZiroFormsValidationState.VALID;
    },
  };

  ngOnInit(): void {
    this._initFormConfig();
    this._initBottomNav();
    const bottomNavErrorStateSub = combineLatest([
      this._validateAndSubmitSource,
      this.ziroFormsUpdateSource.asObservable(),
    ]).subscribe({
      next: () => {
        this._bottomNavService.dispatch(
          new BottomNavUpdateState({
            hasValidationError: !this.isFormValid(),
          }),
        );
      },
    });
    this._subscriptions.add(bottomNavErrorStateSub);
  }

  toEntity = (form: MsSecurityGroupManagementFormData): MsSecurityGroupManagementEntity => {
    return {
      readOnlySecurityGroupList: form.readOnlySecurityGroupList.map(
        (group: MsSecurityGroupManagementSelectFormData) => {
          return {
            description: '',
            displayName: group.value.displayName,
            id: group.value.id,
          };
        },
      ),
      writeSecurityGroupList: form.writeSecurityGroupList.map((group: MsSecurityGroupManagementSelectFormData) => {
        return {
          description: '',
          displayName: group.value.displayName,
          id: group.value.id,
        };
      }),
    };
  };

  toFormData = (entity: MsSecurityGroupManagementEntity): MsSecurityGroupManagementFormData => {
    return {
      readOnlySecurityGroupList: entity.readOnlySecurityGroupList.map(
        (group: MicrosoftSecurityGroupOption): MsSecurityGroupManagementSelectFormData => {
          return {
            label: group.displayName || group.description || group.id,
            value: {
              displayName: group.displayName,
              id: group.id,
              url: null,
            },
          };
        },
      ),
      writeSecurityGroupList: entity.writeSecurityGroupList.map(
        (group: MicrosoftSecurityGroupOption): MsSecurityGroupManagementSelectFormData => {
          return {
            label: group.displayName || group.description || group.id,
            value: {
              displayName: group.displayName,
              id: group.id,
              url: null,
            },
          };
        },
      ),
    };
  };

  protected submit(): Observable<void> {
    return this._onSubmit();
  }

  private _onSubmit(): Observable<void> {
    this._setButtonState(true);
    if (this.entity.readOnlySecurityGroupList.length === 0 && this.entity.writeSecurityGroupList.length === 0) {
      const options = {
        windowClass: 'delete-button-modal',
        modalViewProperties: {
          title: 'tkey;admin.microsoft.security_group_management.modal.confirmation.title',
          promptBody: 'tkey;admin.microsoft.security_group_management.modal.confirmation.description',
          icon: ZiroIcons.WARNING_TRIANGLE,
          iconClass: 'text-warning',
          displayCloseButton: true,
          buttons: [
            {
              label: 'tkey;dialogs.button.cancel',
              buttonClass: ButtonStyles.OUTLINE_DEFAULT,
              dataAutomation: 'confirmation-modal-cancel-button',
            },
            {
              label: 'tkey;dialogs.button.ok',
              buttonClass: ButtonStyles.OUTLINE_PRIMARY,
              dataAutomation: 'confirmation-modal-confirm-button',
              cb: () => of(true),
            },
          ],
        },
      };
      return new Observable((subscriber) => {
        this._ziroModalService
          .openPromptModal(() => options.modalViewProperties, options)
          .subscribe((result) => {
            if (result) {
              this._putSettings().subscribe(() => {
                subscriber.next(null);
                subscriber.complete();
              });
            } else {
              this._setButtonState(false);
              subscriber.next(null);
              subscriber.complete();
            }
          });
      });
    } else {
      return this._putSettings();
    }
  }

  private _putSettings(): Observable<any> {
    return this._microsoftSecurityGroupsSettingsResource.put(this.tenantId(), this.entity).pipe(
      tap(() => {
        this._toastService.push(
          ToastTypes.SUCCESS,
          ZiroIcons.MICROSOFT_365_SMALL,
          'tkey;admin.microsoft.security_group_management.title',
          'tkey;shared.toast.save.success.title',
        );
      }),
      finalize(() => this._setButtonState(false)),
    );
  }

  private _searchGroups(params: SecurityGroupAsyncSearchParams, query: string): Observable<ZiroSelectOption[]> {
    const selectedOptionIds = this.entity[params.entityKey].map((option: MicrosoftSecurityGroupOption) => option.id);

    return this._microsoftSecurityGroupsSearchResource
      .search(this.tenantId().toString(), query, params.assignedOnly)
      .pipe(
        map((results: MicrosoftSecurityGroupResult[]) => {
          return results
            .filter((result: MicrosoftSecurityGroupResult) => {
              return !selectedOptionIds.includes(result.microsoftSecurityGroupRefJson.id);
            })
            .map((result: MicrosoftSecurityGroupResult) => {
              return {
                label: result.microsoftSecurityGroupRefJson.displayName,
                value: result.microsoftSecurityGroupRefJson,
              };
            });
        }),
      );
  }

  private _initFormConfig(): void {
    this.formConfig = {
      fields: {
        readOnlySecurityGroupList: {
          label: 'tkey;admin.microsoft.security_group_management.read_groups.label',
          dataAutomation: 'ms-security-group-management-read-groups',
          componentConfig: new ZiroSelectConfig({
            isMultiSelect: true,
            asyncOptionsFn: this._searchGroups.bind(this, {
              assignedOnly: false,
              entityKey: 'readOnlySecurityGroupList',
            }),
            minSearchLength: 3,
            placeholder: 'tkey;admin.microsoft.security_group_management.read_groups.placeholder',
          }),
          helpText: 'tkey;admin.microsoft.security_group_management.read_groups.helptext',
          validation: [
            {
              validator: this._validators.noOverlaps,
              message: () => this._overlappingErrorMessage,
              injectValuesFromFields: ['writeSecurityGroupList'],
            },
            {
              validator: this._validators.noDeletedValues,
              message: () => {
                const translation = this._translateService.instant('tkey;select.invalid_options.label');

                const invalidOptions = this.formData.readOnlySecurityGroupList
                  .filter((group) => !group.value.displayName)
                  .map((group) => group.label);
                return `${translation} ${invalidOptions.join(', ')}`;
              },
            },
          ],
        },
        writeSecurityGroupList: {
          label: 'tkey;admin.microsoft.security_group_management.write_groups.label',
          dataAutomation: 'ms-security-group-management-write-groups',
          componentConfig: new ZiroSelectConfig({
            isMultiSelect: true,
            asyncOptionsFn: this._searchGroups.bind(this, {
              assignedOnly: true,
              entityKey: 'writeSecurityGroupList',
            }),
            minSearchLength: 3,
            placeholder: 'tkey;admin.microsoft.security_group_management.write_groups.placeholder',
          }),
          helpText: 'tkey;admin.microsoft.security_group_management.write_groups.helptext',
          validation: [
            {
              validator: this._validators.noOverlaps,
              message: () => this._overlappingErrorMessage,
              injectValuesFromFields: ['readOnlySecurityGroupList'],
            },
            {
              validator: this._validators.noDeletedValues,
              message: () => {
                const translation = this._translateService.instant('tkey;select.invalid_options.label');

                const invalidOptions = this.formData.writeSecurityGroupList
                  .filter((group) => !group.value.displayName)
                  .map((group) => group.label);
                return `${translation} ${invalidOptions.join(', ')}`;
              },
            },
          ],
        },
      },
    };
  }

  private _initBottomNav(): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonsList([
        {
          id: 'security-group-management-save',
          dataAutomation: 'security-group-management-save',
          label: 'tkey;global.button.save.text',
          icon: ZiroIcons.SAVE_SUCCESS,
          buttonClass: ButtonStyles.PRIMARY,
          type: ButtonTypes.SUBMIT,
          submitSubject: this._validateAndSubmitSource,
        },
      ]),
    );
  }

  private _setButtonState(setting: boolean): void {
    this._bottomNavService.setButtonPendingState('security-group-management-save', setting);
  }
}
